import { Component } from '@theme/component'

const ScrollbarConfig = {
  minThumbHeight: 56
}

function mapRange(inMin, inMax, value, outMin, outMax) {
  return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin
}

function clamp(min, max, value) {
  return Math.max(min, Math.min(max, value))
}

const isCoarsePointer = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches

class ScrollbarElement extends Component {
  requiredRefs = ['inner', 'thumb']
  metrics = null
  metricsFrame = null
  positionFrame = null
  resizeObserver = null
  dragInnerTop = null
  lastDisabled = null
  lastThumbHeight = null
  lastThumbOffset = null
  start = null
  isInteractive = false

  connectedCallback() {
    super.connectedCallback()

    this.isInteractive = !isCoarsePointer()
    this.refreshScrollbarState()
    this.observeScrollbarMetrics()

    if (this.isInteractive) {
      this.refs.thumb.addEventListener('pointerdown', this.onPointerDown, false)
      window.addEventListener('pointermove', this.onPointerMove, false)
      window.addEventListener('pointerup', this.onPointerUp, false)
    }

    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onResize, { passive: true })
    window.visualViewport?.addEventListener('resize', this.onResize, {
      passive: true
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this.isInteractive) {
      this.refs.thumb.removeEventListener(
        'pointerdown',
        this.onPointerDown,
        false
      )
      window.removeEventListener('pointermove', this.onPointerMove, false)
      window.removeEventListener('pointerup', this.onPointerUp, false)
    }

    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onResize)
    window.visualViewport?.removeEventListener('resize', this.onResize)
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.cancelPendingFrames()
  }

  onScroll = () => {
    if (this.positionFrame !== null) {
      return
    }

    this.positionFrame = requestAnimationFrame(() => {
      this.positionFrame = null
      this.updateThumbPosition()
    })
  }

  onResize = () => {
    this.queueScrollbarRefresh()
  }

  observeScrollbarMetrics() {
    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const targets = [this.refs.inner, document.documentElement, document.body]
    this.resizeObserver = new ResizeObserver(this.queueScrollbarRefresh)

    for (const target of targets) {
      if (!(target instanceof Element)) {
        continue
      }
      this.resizeObserver.observe(target)
    }
  }

  queueScrollbarRefresh = () => {
    if (this.metricsFrame !== null) {
      return
    }

    this.metricsFrame = requestAnimationFrame(() => {
      this.metricsFrame = null
      this.refreshScrollbarState()
    })
  }

  refreshScrollbarState() {
    this.metrics = this.getMetrics()
    this.applyScrollbarMetrics()
    this.updateThumbPosition()
  }

  applyScrollbarMetrics() {
    const thumb = this.refs.thumb
    const metrics = this.metrics

    if (!(thumb instanceof HTMLElement && metrics !== null)) {
      return
    }

    const disabled = metrics.limit <= 0
    if (this.lastDisabled !== disabled) {
      this.toggleAttribute('data-scrollbar-disabled', disabled)
      this.lastDisabled = disabled
    }

    if (this.lastThumbHeight === metrics.thumbHeight) {
      return
    }

    thumb.style.height = `${metrics.thumbHeight}px`
    this.lastThumbHeight = metrics.thumbHeight
  }

  updateThumbPosition() {
    const thumb = this.refs.thumb
    const metrics = this.metrics

    if (!(thumb instanceof HTMLElement && metrics !== null)) {
      return
    }

    const { limit, maxOffset } = metrics
    const progress = limit > 0 ? clamp(0, 1, window.scrollY / limit) : 0
    const thumbOffset = progress * maxOffset

    if (this.lastThumbOffset === thumbOffset) {
      return
    }

    thumb.style.transform = `translate3d(0,${thumbOffset}px,0)`
    this.lastThumbOffset = thumbOffset
  }

  onPointerMove = (e) => {
    const metrics = this.metrics

    if (this.start === null || metrics === null) {
      return
    }
    e.preventDefault()

    if (this.dragInnerTop === null) {
      return
    }

    const { limit, maxOffset } = metrics
    const thumbOffset = clamp(
      0,
      maxOffset,
      e.clientY - this.dragInnerTop - this.start
    )
    const scroll = mapRange(0, maxOffset || 1, thumbOffset, 0, limit)

    window.scrollTo({ behavior: 'auto', top: scroll })
  }

  onPointerDown = (e) => {
    if (e.button !== 0) {
      return
    }

    const thumb = this.refs.thumb

    if (!(thumb instanceof HTMLElement)) {
      return
    }

    e.preventDefault()
    const metrics = this.getMetrics()
    const innerTop = this.getInnerTop()
    if (metrics === null || innerTop === null) {
      return
    }

    this.metrics = metrics
    this.dragInnerTop = innerTop
    this.applyScrollbarMetrics()
    this.updateThumbPosition()
    this.start = this.getThumbDragOffset(e.clientY)
    thumb.setPointerCapture(e.pointerId)
    document.documentElement.classList.add('scrollbar-grabbing')
  }

  onPointerUp = () => {
    this.start = null
    this.dragInnerTop = null
    document.documentElement.classList.remove('scrollbar-grabbing')
  }

  getMetrics() {
    const inner = this.refs.inner
    const thumb = this.refs.thumb

    if (!(inner instanceof HTMLElement && thumb instanceof HTMLElement)) {
      return null
    }

    const innerHeight = inner.offsetHeight
    const scrollHeight = Math.max(
      document.documentElement.scrollHeight,
      window.innerHeight
    )
    const limit = Math.max(0, scrollHeight - window.innerHeight)
    const proportionalHeight = (window.innerHeight / scrollHeight) * innerHeight
    const thumbHeight = clamp(
      ScrollbarConfig.minThumbHeight,
      innerHeight,
      proportionalHeight
    )

    return {
      limit,
      maxOffset: Math.max(0, innerHeight - thumbHeight),
      thumbHeight
    }
  }

  getInnerTop() {
    const inner = this.refs.inner
    if (!(inner instanceof HTMLElement)) {
      return null
    }

    return inner.getBoundingClientRect().top
  }

  getThumbDragOffset(clientY) {
    const metrics = this.metrics
    if (metrics === null || this.dragInnerTop === null) {
      return 0
    }

    const progress = metrics.limit > 0 ? window.scrollY / metrics.limit : 0
    const thumbTop = this.dragInnerTop + progress * metrics.maxOffset
    return clientY - thumbTop
  }

  cancelPendingFrames() {
    if (this.positionFrame !== null) {
      cancelAnimationFrame(this.positionFrame)
      this.positionFrame = null
    }
    if (this.metricsFrame !== null) {
      cancelAnimationFrame(this.metricsFrame)
      this.metricsFrame = null
    }
  }
}

if (!customElements.get('scroll-bar')) {
  customElements.define('scroll-bar', ScrollbarElement)
}
